import type { Query, QueryClient, QueryKey } from '@tanstack/react-query';
import { getSdkAuthStore } from '../auth/state/auth-state.js';
import { refetchRealtimeQuery } from '../core/internal/refetchRealtimeQuery.js';

interface ContentBlockingVars {
  readonly userId: string;
}

interface ContentBlockingResponse {
  readonly blocked: boolean;
  readonly changed: boolean;
  readonly version: string;
}

interface HiddenMarketVars {
  readonly marketPubkey: string;
}

interface HiddenMarketResponse {
  readonly hidden: boolean;
  readonly changed: boolean;
  readonly version: string;
}

const PROTECTED_QUERY_SPECS: ReadonlySet<string> = new Set([
  'useGetMarkets',
  'useGetWinners',
  'useGetMarket',
  'useSimilarMarketsByMarket',
  'useSimilarWinnersByMarket',
  'useGetTheses',
  'useGetThesis',
  'useGetProposals',
  'useGetProposal',
  'useGetSearch',
  'useGetUser',
  'useGetFollows',
  'useGetUserMarkets',
  'useGetOutcomeSuggestions',
]);
const DIRECT_PROTECTED_QUERY_SPECS: ReadonlySet<string> = new Set([
  'useGetMarket',
  'useGetProposal',
  'useGetThesis',
  'useGetUser',
]);

const latestVersionByViewer = new Map<string, bigint>();

export function isContentBlockingAffectedQuery(query: Query): boolean {
  const specName = query.meta?.specName;
  return typeof specName === 'string' && PROTECTED_QUERY_SPECS.has(specName);
}

export function isBlockedAccountCheckQuery(query: Query): boolean {
  return query.meta?.specName === 'useGetBlockedAccountCheck';
}

export function isHiddenMarketCheckQuery(query: Query): boolean {
  return query.meta?.specName === 'useGetHiddenMarketCheck';
}

export function applyBlockAccountOptimistic<T>(
  previous: T,
  vars: ContentBlockingVars,
  queryKey: QueryKey,
): T {
  if (isRelationshipManagementQueryKey(queryKey)) return previous;
  return removeBlockedAccountContent(previous, vars.userId, queryKey);
}

export function applyUnblockAccountOptimistic<T>(
  previous: T,
  _vars: ContentBlockingVars,
  _queryKey: QueryKey,
): T {
  return previous;
}

export function applyHideMarketOptimistic<T>(
  previous: T,
  vars: HiddenMarketVars,
  queryKey: QueryKey,
): T {
  if (isRelationshipManagementQueryKey(queryKey)) return previous;
  return removeHiddenMarketContent(previous, vars.marketPubkey, queryKey);
}

export function applyUnhideMarketOptimistic<T>(
  previous: T,
  _vars: HiddenMarketVars,
  _queryKey: QueryKey,
): T {
  return previous;
}

export async function reconcileContentBlockingResponse(
  queryClient: QueryClient,
  response: ContentBlockingResponse,
  vars: ContentBlockingVars,
): Promise<void> {
  const version = parseVersion(response.version);
  const viewerKey = getSdkAuthStore().current().user?.id ?? 'unscoped';
  const latestVersion = latestVersionByViewer.get(viewerKey);
  if (version === null || (latestVersion !== undefined && version < latestVersion)) {
    await invalidateProtectedQueries(queryClient, vars.userId);
    return;
  }

  latestVersionByViewer.set(viewerKey, version);
  await invalidateAfterContentBlockingResponse(queryClient, response.blocked);
}

export async function reconcileHiddenMarketResponse(
  queryClient: QueryClient,
  response: HiddenMarketResponse,
  vars: HiddenMarketVars,
): Promise<void> {
  const version = parseVersion(response.version);
  const viewerKey = getSdkAuthStore().current().user?.id ?? 'unscoped';
  const latestVersion = latestVersionByViewer.get(viewerKey);
  if (version === null || (latestVersion !== undefined && version < latestVersion)) {
    await invalidateHiddenMarketQueries(queryClient, vars.marketPubkey);
    return;
  }

  latestVersionByViewer.set(viewerKey, version);
  await invalidateAfterHiddenMarketResponse(queryClient, response.hidden);
}

export function __resetContentBlockingVersionsForTests(): void {
  latestVersionByViewer.clear();
}

function parseVersion(value: string): bigint | null {
  return /^\d+$/.test(value) ? BigInt(value) : null;
}

async function invalidateProtectedQueries(
  queryClient: QueryClient,
  targetUserId: string,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ predicate: isProtectedContentQuery }),
    queryClient.invalidateQueries({ predicate: isBlockedAccountsManagementQuery }),
    queryClient.invalidateQueries({
      predicate: (query) =>
        isBlockedAccountCheckQuery(query) && isTargetCheck(query.queryKey, targetUserId),
    }),
  ]);
}

async function invalidateHiddenMarketQueries(
  queryClient: QueryClient,
  marketPubkey: string,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ predicate: isProtectedContentQuery }),
    queryClient.invalidateQueries({ predicate: isHiddenMarketsManagementQuery }),
    queryClient.invalidateQueries({
      predicate: (query) =>
        isHiddenMarketCheckQuery(query) && isTargetHiddenMarketCheck(query.queryKey, marketPubkey),
    }),
  ]);
}

async function invalidateAfterContentBlockingResponse(
  queryClient: QueryClient,
  blocked: boolean,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ predicate: isBlockedAccountsManagementQuery }),
    blocked
      ? queryClient.invalidateQueries({
          predicate: isDirectProtectedContentQuery,
          refetchType: 'none',
        })
      : invalidateAndRefetchProtectedQueries(queryClient, isProtectedContentQuery),
  ]);
}

async function invalidateAfterHiddenMarketResponse(
  queryClient: QueryClient,
  hidden: boolean,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ predicate: isHiddenMarketsManagementQuery }),
    hidden
      ? queryClient.invalidateQueries({
          predicate: isDirectProtectedContentQuery,
          refetchType: 'none',
        })
      : invalidateAndRefetchProtectedQueries(queryClient, isProtectedContentQuery),
  ]);
}

async function invalidateAndRefetchProtectedQueries(
  queryClient: QueryClient,
  predicate: (query: Query) => boolean,
): Promise<void> {
  await queryClient.invalidateQueries({ predicate, refetchType: 'none' });
  const activeQueries = queryClient
    .getQueryCache()
    .getAll()
    .filter((query) => query.observers.length > 0 && predicate(query));
  await Promise.all(
    activeQueries.map((query) => refetchRealtimeQuery(queryClient, query.queryKey)),
  );
}

function isProtectedContentQuery(query: Query): boolean {
  const specName = query.meta?.specName;
  return typeof specName === 'string' && PROTECTED_QUERY_SPECS.has(specName);
}

function isDirectProtectedContentQuery(query: Query): boolean {
  const specName = query.meta?.specName;
  return typeof specName === 'string' && DIRECT_PROTECTED_QUERY_SPECS.has(specName);
}

function isBlockedAccountsManagementQuery(query: Query): boolean {
  return query.meta?.specName === 'useGetBlockedAccounts';
}

function isHiddenMarketsManagementQuery(query: Query): boolean {
  return query.meta?.specName === 'useGetHiddenMarkets';
}

function isTargetCheck(queryKey: QueryKey, targetUserId: string): boolean {
  return (
    queryKey[0] === 'blocked-accounts' && queryKey[1] === 'detail' && queryKey[2] === targetUserId
  );
}

function isTargetHiddenMarketCheck(queryKey: QueryKey, marketPubkey: string): boolean {
  return (
    queryKey[0] === 'hidden-markets' && queryKey[1] === 'detail' && queryKey[2] === marketPubkey
  );
}

function isRelationshipManagementQueryKey(queryKey: QueryKey): boolean {
  return (
    (queryKey[0] === 'blocked-accounts' && queryKey[1] === 'list') ||
    (queryKey[0] === 'hidden-markets' && queryKey[1] === 'list')
  );
}

function removeHiddenMarketContent<T>(previous: T, marketPubkey: string, queryKey: QueryKey): T {
  if (previous === null || previous === undefined) return previous;
  if (isDirectTargetMarket(previous, marketPubkey, queryKey)) return null as T;
  if (Array.isArray(previous)) return filterMarketRows(previous, marketPubkey, queryKey) as T;
  if (!isRecord(previous)) return previous;

  let next: Record<string, unknown> = previous;
  let changed = false;

  if (Object.hasOwn(previous, marketPubkey) && previous[marketPubkey] !== null) {
    next = { ...next, [marketPubkey]: null };
    changed = true;
  }

  for (const field of ['pages', 'data'] as const) {
    const value = previous[field];
    if (value === undefined) continue;
    const filtered = removeHiddenMarketContent(value, marketPubkey, queryKey);
    if (filtered === value) continue;
    if (!changed) next = { ...next };
    next[field] = filtered;
    changed = true;
  }

  for (const field of [
    'items',
    'rows',
    'replies',
    'suggestions',
    'proposals',
    'markets',
    'winners',
    'outcomes',
    'blended',
  ] as const) {
    const rows = previous[field];
    if (!Array.isArray(rows)) continue;
    const filtered = filterMarketRows(rows, marketPubkey, queryKey);
    if (filtered === rows) continue;
    if (!changed) next = { ...next };
    next[field] = filtered;
    changed = true;
  }

  return (changed ? next : previous) as T;
}

function filterMarketRows<T>(
  rows: readonly T[],
  marketPubkey: string,
  queryKey: QueryKey,
): readonly T[] {
  let changed = false;
  const filtered: T[] = [];
  for (const row of rows) {
    if (
      isMarketRepresentation(row, marketPubkey) ||
      isNormalizedMarketIndexEntry(row, marketPubkey, queryKey)
    ) {
      changed = true;
      continue;
    }
    const next = removeHiddenMarketContent(row, marketPubkey, queryKey);
    filtered.push(next);
    if (next !== row) changed = true;
  }
  return changed ? filtered : rows;
}

function isNormalizedMarketIndexEntry(
  value: unknown,
  marketPubkey: string,
  queryKey: QueryKey,
): boolean {
  return queryKey[0] === 'markets' && value === marketPubkey;
}

function isDirectTargetMarket(value: unknown, marketPubkey: string, queryKey: QueryKey): boolean {
  if (!isMarketRepresentation(value, marketPubkey)) return false;
  return queryKey[1] === 'detail' && (queryKey[0] === 'markets' || queryKey[0] === 'proposals');
}

function isMarketRepresentation(value: unknown, marketPubkey: string): boolean {
  if (!isRecord(value)) return false;
  return (
    value.pubkey === marketPubkey ||
    value.marketPubkey === marketPubkey ||
    value.market === marketPubkey ||
    value.proposalId === marketPubkey
  );
}

function removeBlockedAccountContent<T>(previous: T, targetUserId: string, queryKey: QueryKey): T {
  if (previous === null || previous === undefined) return previous;
  if (isDirectTargetUser(previous, targetUserId, queryKey)) return null as T;
  if (isAuthoredBy(previous, targetUserId)) return null as T;
  if (Array.isArray(previous)) return filterRows(previous, targetUserId) as T;
  if (!isRecord(previous)) return previous;

  let next: Record<string, unknown> = previous;
  let changed = false;

  if (Array.isArray(previous.pages)) {
    const previousPages = previous.pages;
    const pages = previousPages.map((page) =>
      removeBlockedAccountContent(page, targetUserId, queryKey),
    );
    if (pages.some((page, index) => page !== previousPages[index])) {
      next = { ...next, pages };
      changed = true;
    }
  }

  if (Array.isArray(previous.data)) {
    const data = isTargetCreatorCollection(previous.data, targetUserId, queryKey)
      ? []
      : filterRows(previous.data, targetUserId);
    if (data !== previous.data) {
      next = { ...next, data };
      changed = true;
    }
  }

  for (const field of [
    'replies',
    'suggestions',
    'users',
    'creators',
    'proposals',
    'markets',
    'winners',
    'outcomes',
    'blended',
  ] as const) {
    const rows = previous[field];
    if (!Array.isArray(rows)) continue;
    const filtered = filterRows(rows, targetUserId);
    if (filtered === rows) continue;
    if (!changed) next = { ...next };
    next[field] = filtered;
    changed = true;
  }

  return (changed ? next : previous) as T;
}

function isDirectTargetUser(previous: unknown, targetUserId: string, queryKey: QueryKey): boolean {
  return (
    queryKey[0] === 'users' &&
    queryKey[1] === 'detail' &&
    queryKey[2] === targetUserId &&
    isRecord(previous) &&
    ('displayName' in previous || 'handle' in previous || 'creatorProfile' in previous)
  );
}

function isTargetCreatorCollection(
  rows: readonly unknown[],
  targetUserId: string,
  queryKey: QueryKey,
): boolean {
  if (queryKey[0] !== 'users' || queryKey[1] !== 'list' || queryKey[2] !== targetUserId) {
    return false;
  }
  return (
    rows.length === 0 || rows.some((row) => isRecord(row) && 'title' in row && 'pubkey' in row)
  );
}

function filterRows<T>(rows: readonly T[], targetUserId: string): readonly T[] {
  const filtered = rows.filter((row) => !isAuthoredBy(row, targetUserId));
  return filtered.length === rows.length ? rows : filtered;
}

function isAuthoredBy(value: unknown, targetUserId: string): boolean {
  if (!isRecord(value)) return false;
  if (value.userId === targetUserId) return true;

  for (const field of [
    'user',
    'creator',
    'creatorProfile',
    'proposer',
    'trader',
    'author',
    'suggester',
  ] as const) {
    const identity = value[field];
    if (isRecord(identity) && identity.userId === targetUserId) return true;
  }
  return false;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
